/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import { Button, Input, FormPro } from '@roo/roo'
import CommonTable from '@/components/CommonTable'
import TipText from '@/components/TipText'
import { fetchBalanceList, exportBalanceList, fetchBatchAdjustAsyncResult } from '@/pages/balance/utils/api'
import { IBalanceItem, IBalanceListParams, IExportTaskRes } from '@/pages/balance/utils/types'
import { getStaffStatusText } from '@/pages/balance/utils/constants'
import { downloadFile } from '@/common/utils'
import { failToast } from '@/common/utils/toast'
import { excelPolling } from '@/common/utils/polling'
import BalanceAdjust from './BalanceAdjust'
import BatchAdjustModal from './BatchAdjustModal'
import BatchRecordDrawer from './BatchRecordDrawer'
import styles from './index.module.less'
import { isEn, replaceMoneySymbol } from '@/common/utils/i18nFuncs'

interface BalanceListTabProps {
  /** 津贴策略编号 */
  budgetPolicyNo: string
  /** 规则包 ID（用于批量调整上传、模板下载、历史记录查询） */
  rulePackageId: string
  /** 下发周期类型：0=一次性，1+=循环 */
  periodType: number
  /** 是否为一次性津贴（true：显示余额调整、批量调整按钮） */
  isOnceType: boolean
  /** 跳转变动明细时回调（携带 email 或 phone 作为 keyword） */
  onViewTransactions?: (keyword: string) => void
}

const BalanceListTab: React.FC<BalanceListTabProps> = ({
  budgetPolicyNo,
  rulePackageId,
  periodType,
  isOnceType,
  onViewTransactions,
}) => {
  const { t } = useTranslation()
  const [accountInput, setAccountInput] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 20 })
  const [filterParams, setFilterParams] = useState<Partial<IBalanceListParams>>({})
  const [batchModalVisible, setBatchModalVisible] = useState(false)
  const [batchRecordDrawerVisible, setBatchRecordDrawerVisible] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  const queryParams: IBalanceListParams = {
    budgetPolicyNo,
    ...pageInfo,
    ...filterParams,
  }

  const { data: listRes, loading, run: fetchList } = useRequest(
    () => fetchBalanceList(queryParams),
    { refreshDeps: [budgetPolicyNo, pageInfo, filterParams] }
  )

  const handleSearch = () => {
    const keyword = accountInput.trim() || nameInput.trim() || undefined
    setFilterParams({ keyword })
    setPageInfo(prev => ({ ...prev, pageNo: 1 }))
  }

  const handleExport = async () => {
    try {
      setExportLoading(true)
      const keyword = accountInput.trim() || nameInput.trim() || undefined
      const res = await exportBalanceList({
        budgetPolicyNo,
        keyword,
      })
      handleExportResult(res)
    } catch (error: any) {
      failToast(error?.message || t('Keeta_EE_export_try_again_nDBU', '导出失败，请稍后重试！'))
      setExportLoading(false)
    }
  }

  const handleExportResult = (res: IExportTaskRes) => {
    if (!res.async && res.fileURL) {
      downloadFile({ url: res.fileURL })
      setExportLoading(false)
    } else if (res.async && res.taskToken) {
      excelPolling({
        requestFunction: () => fetchBatchAdjustAsyncResult(res.taskToken as string),
        onTimeoutChange: () => {},
        onFinish: (param) => {
          setExportLoading(false)
          if (param.type === 'success' && param.data?.fileUrl) {
            downloadFile({ url: param.data.fileUrl })
          } else {
            failToast(param.data?.message || t('Keeta_EE_export_try_again_nDBU', '导出失败，请稍后重试！'))
          }
        },
      })
    } else {
      setExportLoading(false)
    }
  }

  const handleAdjustSuccess = () => {
    fetchList()
  }

  const getActionColWidth = () => {
    if (isOnceType) return isEn() ? 260 : 160
    return isEn() ? 160 : 80
  }
  const actionColWidth = getActionColWidth()

  const columns = [
    {
      title: t('e_pc_account_id', '账号'),
      dataIndex: 'email',
      width: 200,
      render: (_: string, record: IBalanceItem) => (
        <TipText text={record.email || record.phone || '--'} />
      ),
    },
    {
      title: t('staff_name', '姓名'),
      dataIndex: 'staffName',
      width: 180,
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      title: t('staff_department', '部门'),
      dataIndex: 'department',
      width: 180,
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      title: t('staff_status', '状态'),
      dataIndex: 'staffStatus',
      width: 100,
      render: (status: string) => getStaffStatusText(status, t),
    },
    {
      title: t('e_pc_total_disburse_amount', '累计下发金额'),
      dataIndex: 'totalDeliveryAmount',
      width: 180,
      render: (val: IBalanceItem['totalDeliveryAmount']) => <TipText text={replaceMoneySymbol(val?.displayText || '') || '--'} />,
    },
    {
      title: t('e_pc_expired_amount', '失效金额'),
      dataIndex: 'totalExpiredAmount',
      width: 120,
      render: (val: IBalanceItem['totalExpiredAmount']) => <TipText text={replaceMoneySymbol(val?.displayText || '') || '--'} />,
    },
    {
      title: t('e_pc_available_balance', '有效余额'),
      dataIndex: 'totalAvailableAmount',
      width: 160,
      render: (val: IBalanceItem['totalAvailableAmount']) => <TipText text={replaceMoneySymbol(val?.displayText || '') || '--'} />,
    },
    {
      title: t('e_pc_action_col', '操作'),
      dataIndex: 'budgetFeeId',
      fixed: 'right',
      width: actionColWidth,
      render: (_: any, record: IBalanceItem) => (
        <div className={styles.action_cell}>
          {isOnceType && (
            <>
              <BalanceAdjust
                record={record}
                budgetPolicyNo={budgetPolicyNo}
                onSuccess={handleAdjustSuccess}
              />
              &ensp;&ensp;&ensp;
            </>
          )}
          <Button
            type="brand-text"
            onClick={() => onViewTransactions?.(record.email || record.phone || '')}
          >
            {t('e_pc_change_details', '变动明细')}
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* 筛选区：CSS Grid 4 列等分，与员工管理风格一致 */}
      <FormPro layout="vertical" size="small" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
        <div className={styles.filterGrid} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px' }}>
          <FormPro.Item label={t('e_pc_account_id', '员工账号')} style={{ marginBottom: 8 }}>
            <Input
              allowClear
              style={{ width: '100%' }}
              value={accountInput}
              placeholder={t('e_pc_placeholder_account', '搜索邮箱或手机号')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {if (e.key === 'Enter') handleSearch()}}
            />
          </FormPro.Item>
          <FormPro.Item label={t('staff_name', '员工姓名')} style={{ marginBottom: 8 }}>
            <Input
              allowClear
              style={{ width: '100%' }}
              value={nameInput}
              placeholder={t('e_pc_placeholder_name', '搜索姓名')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {if (e.key === 'Enter') handleSearch()}}
            />
          </FormPro.Item>
          {/* 查询按钮占第 3 列底部对齐，不放批量操作（避免溢出） */}
          <div className={styles.actionColRight} style={{ gridColumn: 'span 2' }}>
            <div style={{ marginBottom: 8 }}>
              <Button type="brand" size="normal" onClick={handleSearch} className="btn-dark">
                {t('e_pc_aquire', '查询')}
              </Button>
            </div>
          </div>
        </div>
      </FormPro>

      {/* 操作行：批量调整居左，导出居右，位于搜索区与表格之间 */}
      <div className={styles.actionBar} style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {isOnceType && (
            <>
              <Button size="normal" onClick={() => setBatchRecordDrawerVisible(true)}>
                {t('e_pc_bulk_adjust_records', '批量调整记录')}
              </Button>
              <Button size="normal" type="brand" onClick={() => setBatchModalVisible(true)}>
                {t('e_pc_bulk_adjust_balance', '批量调整余额')}
              </Button>
            </>
          )}
        </div>
        <div className={styles.actionBarRight}>
          <Button className={styles.actionBarSecondaryBtn} loading={exportLoading} onClick={handleExport}>
            {t('e_pc_action_export', '导出')}
          </Button>
        </div>
      </div>

      {/* 表格 */}
      <CommonTable
        rowKey="budgetFeeId"
        columns={columns}
        data={listRes?.result || []}
        loading={loading}
        style={{ marginTop: 16 }}
        pagination={{
          total: listRes?.totalCount,
          pageSize: pageInfo.pageSize,
          onChange: (pageNo: number, pageSize: number) => setPageInfo({ pageNo, pageSize }),
        }}
      />

      {/* 批量调整弹窗 */}
      <BatchAdjustModal
        visible={batchModalVisible}
        rulePackageId={rulePackageId}
        budgetPolicyNo={budgetPolicyNo}
        onClose={() => setBatchModalVisible(false)}
        onSuccess={() => {
          setBatchModalVisible(false)
          fetchList()
        }}
      />

      {/* 批量调整历史记录抽屉 */}
      <BatchRecordDrawer
        visible={batchRecordDrawerVisible}
        rulePackageId={rulePackageId}
        onClose={() => setBatchRecordDrawerVisible(false)}
      />
    </div>
  )
}

export default BalanceListTab
